import { Badge, Box, Button, Group, Image, Loader, ScrollArea, Stack, Table, Text, ActionIcon, Tooltip } from "@mantine/core";
import { IconPlus, IconEdit, IconTrash, IconPhoto, IconCornerDownRight, IconGripVertical, IconLanguage } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import { useCategories, useReorderCategories } from "../../../shared/api";
import { useRemove } from "../../../shared/api/resources";
import { PageHeader, ResourceForm, confirmDelete, Lightbox, TranslationDrawer, type FormField } from "../../../shared/ui";

/**
 * Kategori formu alanları — `parent` (üst kategori) seçimi dinamik üretilir:
 * mevcut kök kategoriler seçenek olur. Boş bırakılırsa kategori kök olur.
 */
function buildFields(
  categories: Array<{ id: string; name: string; parent: string | null }>,
  editingId: string | null,
): FormField[] {
  // Üst kategori adayları: kendi alt kategorisi olmayan + düzenlenen kategorinin
  // kendisi olmayan kategoriler. (2 seviye derinlik yeterli — kök + alt.)
  const parentOptions = categories
    .filter((c) => c.id !== editingId && !c.parent)
    .map((c) => ({ value: c.id, label: c.name }));

  return [
    { name: "name", label: "Ad", required: true },
    {
      name: "parent",
      label: "Üst Kategori",
      type: "select",
      clearable: true,
      placeholder: "Yok — kök kategori",
      description: "Boş bırakılırsa kök kategori olur (QR menüde üst sekme)",
      options: parentOptions,
    },
    { name: "description", label: "Açıklama", type: "textarea" },
    {
      name: "image",
      label: "Kategori Görseli",
      type: "image",
      description: "QR menüde kategori kartında gösterilir — küçük türevi (thumbnail) otomatik üretilir",
    },
    { name: "sort_order", label: "Sıra", type: "number", defaultValue: 0 },
    { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
    {
      name: "is_qr_visible",
      label: "QR Menüde Görünür",
      type: "switch",
      defaultValue: true,
      // Kapalıysa bu kategori ve altındaki tüm ürünler QR menüde gösterilmez;
      // POS/admin'de görünmeye devam eder (Okey, maç yayını gibi salon-içi
      // kategoriler için). Üst kategori kapatılırsa alt kategoriler de gizlenir.
      description: "Kapalıysa kategori QR menüde gösterilmez (POS/admin'de görünür)",
    },
  ];
}

interface Cat {
  id: string;
  name: string;
  parent: string | null;
  description?: string;
  image?: string | null;
  image_thumbnail?: string | null;
  sort_order: number;
  is_active: boolean;
  is_qr_visible?: boolean;
}

export function CategoriesPage() {
  const { data, isLoading } = useCategories();
  const remove = useRemove("categories");
  const reorder = useReorderCategories();
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<Cat | null>(null);
  const [translateCat, setTranslateCat] = useState<any | null>(null);
  // Görsele tıklayınca açılan tam ekran önizleme (tek görsel).
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // Sürüklenen satırın id'si — görsel geri bildirim için.
  const [dragId, setDragId] = useState<string | null>(null);
  // data?.results / [] her render'da yeni referans üretir; useMemo ile
  // stabilize et — aksi halde orderedRows useMemo + setRows effect sonsuz
  // döngüye girer ("Maximum update depth exceeded").
  const list: Cat[] = useMemo(
    () => data?.results || data || [],
    [data],
  );

  // Hiyerarşik sıralama: her kökü, hemen ardından kendi alt kategorileriyle diz.
  const orderedRows = useMemo(() => {
    const roots = list
      .filter((c) => !c.parent)
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    const rows: Array<{ cat: Cat; depth: number }> = [];
    for (const root of roots) {
      rows.push({ cat: root, depth: 0 });
      const children = list
        .filter((c) => c.parent === root.id)
        .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
      for (const child of children) {
        rows.push({ cat: child, depth: 1 });
      }
    }
    // parent'ı listede olmayan yetim kayıtlar (savunma amaçlı) sona eklenir
    const placed = new Set(rows.map((r) => r.cat.id));
    for (const c of list) {
      if (!placed.has(c.id)) rows.push({ cat: c, depth: 0 });
    }
    return rows;
  }, [list]);

  // Sürükleme sırasında anlık yeniden dizilen yerel satır listesi.
  const [rows, setRows] = useState(orderedRows);
  useEffect(() => {
    setRows(orderedRows);
  }, [orderedRows]);

  // Sürükle-bırak yalnızca aynı parent'a sahip kardeşler arasında izinli —
  // bir kök kategoriyi alt kategori yapmak / tersi bu basit endpoint'in
  // kapsamı dışında (parent değişikliği form üzerinden yapılmalı).
  function sameParent(a: Cat, b: Cat) {
    return (a.parent || null) === (b.parent || null);
  }

  function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const from = rows.findIndex((r) => r.cat.id === dragId);
    const to = rows.findIndex((r) => r.cat.id === targetId);
    if (from === -1 || to === -1) {
      setDragId(null);
      return;
    }
    // Sadece aynı seviye + aynı parent ile takas
    if (!sameParent(rows[from].cat, rows[to].cat)) {
      notifications.show({
        message: "Sadece aynı seviyedeki kategoriler arasında sıralama yapılabilir",
        color: "yellow",
      });
      setDragId(null);
      return;
    }
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
    setDragId(null);

    // Backend'e tam sırayı gönder — sort_order 0,1,2,… atanır.
    reorder.mutate(
      next.map((r) => r.cat.id),
      {
        onSuccess: () =>
          notifications.show({ message: "Sıralama kaydedildi", color: "green" }),
        onError: () => {
          notifications.show({ message: "Sıralama kaydedilemedi", color: "red" });
          setRows(orderedRows);
        },
      },
    );
  }

  const FIELDS = buildFields(list, editing?.id || null);

  async function handleDelete(id: string) {
    if (!(await confirmDelete("Kategori silinsin mi?"))) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Kategoriler"
        subtitle="Menü kategorilerini düzenleyin — üst/alt kategori (QR menüde sekme + grid)"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>
            Yeni Kategori
          </Button>
        }
      />
      {isLoading ? <Loader /> : (
        <ScrollArea>
          <Text size="xs" c="dimmed" mb={6}>
            Satırları tutamaçtan (⠿) sürükleyerek sırayı değiştirebilirsiniz — sıralama aynı seviyedeki kardeşler arasında çalışır.
          </Text>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={36} />
                <Table.Th w={64}>Görsel</Table.Th>
                <Table.Th>Ad</Table.Th>
                <Table.Th>Açıklama</Table.Th>
                <Table.Th w={80}>Sıra</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th w={90}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(({ cat: c, depth }) => (
                <Table.Tr
                  key={c.id}
                  draggable
                  onDragStart={() => setDragId(c.id)}
                  onDragOver={(e) => dragId && e.preventDefault()}
                  onDrop={() => handleDrop(c.id)}
                  onDragEnd={() => setDragId(null)}
                  {...(dragId === c.id ? { bg: "var(--mantine-color-blue-0)" } : {})}
                  style={{ opacity: dragId === c.id ? 0.5 : 1 }}
                >
                  <Table.Td>
                    <IconGripVertical
                      size={16}
                      color="var(--mantine-color-gray-5)"
                      style={{ cursor: "grab" }}
                    />
                  </Table.Td>
                  <Table.Td>
                    {(c.image_thumbnail || c.image) ? (
                      <Tooltip label="Büyüt" withinPortal>
                        <Image
                          src={c.image_thumbnail || c.image || undefined}
                          h={44}
                          w={44}
                          radius="sm"
                          fit="cover"
                          alt={c.name}
                          style={{ cursor: "zoom-in" }}
                          // Lightbox'ta tam-boy görseli göster (thumbnail değil).
                          onClick={() => c.image && setLightboxUrl(c.image)}
                        />
                      </Tooltip>
                    ) : (
                      <Group justify="center" w={44} h={44} bg="gray.1" style={{ borderRadius: 4 }}>
                        <IconPhoto size={18} color="var(--mantine-color-gray-5)" />
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {/* Alt kategoriler girintili + ok ikonu ile gösterilir */}
                    <Group gap={6} wrap="nowrap" pl={depth ? 28 : 0}>
                      {depth > 0 && (
                        <IconCornerDownRight size={14} color="var(--mantine-color-gray-5)" />
                      )}
                      <Text fw={depth ? 400 : 600}>{c.name}</Text>
                      {depth === 0 && (
                        <Badge size="xs" variant="light" color="blue">üst</Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>{c.description || "-"}</Table.Td>
                  <Table.Td>{c.sort_order}</Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Badge
                        color={c.is_active ? "green" : "gray"}
                        styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}
                      >
                        {c.is_active ? "Aktif" : "Pasif"}
                      </Badge>
                      {c.is_qr_visible === false && (
                        <Badge
                          color="orange"
                          variant="light"
                          styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}
                        >
                          QR gizli
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ whiteSpace: "nowrap", width: 1 }}>
                    <Group gap={4} wrap="nowrap" justify="flex-end">
                      <Tooltip label="Çeviriler (EN)">
                        <ActionIcon variant="subtle" color="grape" onClick={() => setTranslateCat(c)}><IconLanguage size={16} /></ActionIcon>
                      </Tooltip>
                      <ActionIcon variant="subtle" onClick={() => { setEditing(c); open(); }}><IconEdit size={16} /></ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(c.id)}><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {rows.length === 0 && (
            <Box ta="center" py="xl">
              <Text c="dimmed">Henüz kategori yok</Text>
            </Box>
          )}
        </ScrollArea>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="categories"
        title={editing ? "Kategori Düzenle" : "Yeni Kategori"}
        fields={FIELDS}
        editId={editing?.id || null}
        {...(editing ? { initialValues: editing } : {})}
        transform={(v) => ({
          // Boş `parent` → null (DRF boş string'i geçersiz pk sayar)
          ...v,
          parent: v.parent || null,
        })}
      />

      <TranslationDrawer
        opened={!!translateCat}
        onClose={() => setTranslateCat(null)}
        resource="categories"
        id={translateCat?.id}
        title={translateCat ? `${translateCat.name} — Çeviriler (EN)` : "Çeviriler (EN)"}
        baseValues={{
          name: translateCat?.name,
          description: translateCat?.description,
        }}
        translations={translateCat?.translations}
        invalidateKeys={[["categories"]]}
        fields={[
          { name: "name", label: "Ad (EN)" },
          { name: "description", label: "Açıklama (EN)", textarea: true },
        ]}
      />

      {lightboxUrl && (
        <Lightbox
          images={[lightboxUrl]}
          index={0}
          onIndexChange={() => {}}
          onClose={() => setLightboxUrl(null)}
        />
      )}
    </Stack>
  );
}
